import React, { useState } from 'react';
import { ExternalLink, Smartphone, Users, Brain, Mail, CreditCard, Rocket, ArrowRight, Globe, Sparkles, Eye, GraduationCap, MessagesSquare, BookOpen, Bot } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const appsData = [
  {
    id: 'ithr-academy',
    name: 'ITHR Academy',
    tagline: 'Enterprise Agentic AI Credentialing Platform',
    description: "Where the world's workforce learns to command AI. A UAE-built credentialing platform with 8 tiers, 20 industries and 28 role-based courses — anchored to Product, Engineering, Risk, HR, Banking, Healthcare and Executive tracks. AI tutors Aletheia (lesson coach) and Solon (career mentor) guide every learner to a cryptographically-verifiable AI Skills Passport.",
    url: 'https://ithr.online',
    category: 'AI Academy · Enterprise',
    features: ['8 credential tiers × 20 industries', 'Adaptive AI tutor (Aletheia)', 'Career mentor (Solon)', 'Publicly-verifiable AI Skills Passport'],
    icon: GraduationCap,
    gradient: 'from-[#152846]/25 to-gold-400/20',
    accentColor: 'text-[#152846]',
    badgeColor: 'bg-[#152846]/10 text-[#152846]'
  },
  {
    id: 'convrza',
    name: 'Convrza',
    tagline: 'Your Website, Ready to Talk and Act',
    description: 'A conversational layer that drops into any website with a single script and gives every visitor a multilingual AI guide (English + العربية at launch). It answers from your approved content, speaks naturally, captures interest, and knows when to hand off to a human — moving from Answer → Guide → Act with permissions and an audit trail.',
    url: 'https://convrza.com',
    category: 'Conversational AI / SaaS',
    features: ['One-script install on any site', 'English + Arabic voice out of the box', '12 human-like avatars to choose from', 'Grounded answers · human escalation'],
    icon: MessagesSquare,
    gradient: 'from-[#c77b5a]/20 to-[#5b8fa8]/20',
    accentColor: 'text-[#c77b5a]',
    badgeColor: 'bg-[#c77b5a]/10 text-[#c77b5a]'
  },
  {
    id: 'aiilm-academy',
    name: 'AIILM Academy',
    tagline: "Africa's AI Learning Home · Ages 10–16",
    description: "A safe, bilingual (English + Kiswahili) academy where young minds learn artificial intelligence through stories, challenges and projects inspired by the world around them. 120 Africa-relevant courses across three age bands — Mwanzo (10–12), Kati (12–14) and Juu (14–16) — with AI tutors Zuri & Jabari growing alongside every learner.",
    url: 'https://aiilm.academy',
    category: 'EdTech · Youth AI',
    features: ['Ages 10–16 · three progression bands', '120 Africa-relevant courses', 'Bilingual — EN + SW (Kiswahili)', 'Parent-controlled, kid-safe AI tutors'],
    icon: BookOpen,
    gradient: 'from-[#6b8e7f]/25 to-[#c9a84c]/20',
    accentColor: 'text-[#6b8e7f]',
    badgeColor: 'bg-[#6b8e7f]/10 text-[#6b8e7f]'
  },
  {
    id: 'sparkl-aiilm-me',
    name: 'Sparkl',
    tagline: 'AI Literacy for Kids · GCC & MENA',
    description: "Where kids learn AI by playing. Sparkl turns curiosity into confidence for ages 10–16 through bite-sized stories, hands-on model training and remix labs — guided by Sparky, a friendly robot tutor. Rooted in 'ilm (علم — the pursuit of knowledge), Arabic-first when needed, with three age bands: Foundation, Builder and Innovator.",
    url: 'https://aiilm.me',
    category: 'EdTech · GCC Kids',
    features: ['Ages 10–16 across 3 age bands', 'Arabic-first bilingual UI', 'Sparky — friendly robot tutor', 'Five Big Ideas of AI curriculum'],
    icon: Bot,
    gradient: 'from-[#5b8fa8]/25 to-[#8c6e89]/20',
    accentColor: 'text-[#5b8fa8]',
    badgeColor: 'bg-[#5b8fa8]/10 text-[#5b8fa8]'
  },
  {
    id: 'ithr-talentlens',
    name: 'ITHR Talentlens',
    tagline: 'AI-Powered Talent Intelligence Platform',
    description: 'AI-powered talent intelligence platform — assessment, evaluation, and on-demand HR insights for modern teams. Streamline talent decisions with data-driven precision.',
    url: 'https://www.2assistu.com',
    category: 'HR Tech / Assessment',
    features: ['AI-driven candidate assessment', 'Skills evaluation engine', 'On-demand HR insights', 'Talent intelligence dashboard'],
    icon: Eye,
    gradient: 'from-teal-500/20 to-cyan-500/20',
    accentColor: 'text-[#5b8fa8]',
    badgeColor: 'bg-[#5b8fa8]/10 text-[#5b8fa8]'
  },
  {
    id: 'skillsearch',
    name: 'SkillSearch (Qode)',
    tagline: 'Voice-Enabled AI Recruiting Platform',
    description: 'A unified enterprise hiring platform that centralizes the entire recruitment lifecycle — from sourcing and outreach to ATS and AI-powered interviews — replacing disconnected tools with a single engine for speed, precision, and scale.',
    url: 'https://skillsearch-preview.preview.emergentagent.com/',
    category: 'Enterprise SaaS',
    features: ['AI-powered candidate sourcing', 'Smart candidate ranking', 'Automated AI interviews', 'Built-in ATS & job distribution'],
    icon: Users,
    gradient: 'from-blue-500/20 to-cyan-500/20',
    accentColor: 'text-blue-400',
    badgeColor: 'bg-blue-400/10 text-blue-400'
  },
  {
    id: 'ai-clone',
    name: 'AI Digital Clone',
    tagline: "Dr. Srijit's AI-Powered Digital Twin",
    description: 'An AI clone that speaks, thinks, and responds just like Dr. Srijit Nair. Visitors can ask questions, get advice, or have a conversation — powered by a custom cloned voice and personality, available 24/7.',
    url: 'https://replicate-it-2.emergent.host/',
    category: 'AI / Conversational',
    features: ['Personalized AI responses', 'Voice-enabled with cloned voice', 'Customizable personality & tone', 'Always available 24/7'],
    icon: Brain,
    gradient: 'from-purple-500/20 to-pink-500/20',
    accentColor: 'text-purple-400',
    badgeColor: 'bg-purple-400/10 text-purple-400'
  },
  {
    id: 'smartscribe',
    name: 'SmartScribe',
    tagline: 'Intelligent Mobile Note-Taking App',
    description: 'A mobile application built with Expo for smart, AI-assisted note-taking and document management. Available on both iOS and Android via the Expo Go app.',
    url: 'https://app.emergent.sh/share?app=smartscribe-314',
    category: 'Mobile App',
    features: ['Cross-platform (iOS & Android)', 'AI-assisted writing', 'QR code app sharing', 'Native mobile experience'],
    icon: Smartphone,
    gradient: 'from-green-500/20 to-emerald-500/20',
    accentColor: 'text-green-400',
    badgeColor: 'bg-green-400/10 text-green-400'
  },
  {
    id: 'media-leads',
    name: 'Media Leads',
    tagline: 'AI-Powered Lead Generation Platform',
    description: 'A comprehensive lead generation platform designed for media businesses. Capture quality leads, manage them in a centralized dashboard, and leverage AI-powered email assistants for personalized follow-ups and real-time analytics.',
    url: 'https://media-leads.emergent.host/',
    category: 'Marketing / CRM',
    features: ['Lead management dashboard', 'AI email assistant', 'Smart lead scoring', 'Analytics & conversion tracking'],
    icon: Mail,
    gradient: 'from-orange-500/20 to-amber-500/20',
    accentColor: 'text-orange-400',
    badgeColor: 'bg-orange-400/10 text-orange-400'
  },
  {
    id: 'identra',
    name: 'Identra',
    tagline: 'Digital Business Card Platform',
    description: 'Create stunning digital business cards with QR codes and NFC technology. Share contact information instantly, track engagement analytics, and transform professional networking with a modern digital identity solution.',
    url: 'https://identra.co/',
    category: 'Networking / Identity',
    features: ['Smart QR codes with branding', 'NFC tap-to-share cards', 'Real-time scan analytics', 'Print-ready card designs'],
    icon: CreditCard,
    gradient: 'from-gold-400/20 to-yellow-500/20',
    accentColor: 'text-gold-400',
    badgeColor: 'bg-gold-400/10 text-gold-400'
  }
];

const Apps = () => {
  const [hoveredApp, setHoveredApp] = useState(null);

  return (
    <div className="min-h-screen bg-[#faf6ee]" data-testid="apps-page">
      <Header />

      <section className="pt-32 pb-16 bg-[#faf6ee] relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <div className="absolute top-32 left-20 w-80 h-80 bg-gold-400/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-20 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold-400/10 text-gold-400 text-sm font-semibold rounded-full mb-6">
              <Rocket size={16} strokeWidth={2.5} />
              Built & Deployed
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#152846] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Apps <span className="text-gold-400">Portfolio</span>
            </h1>
            <p className="text-lg text-[#5c6878] leading-relaxed max-w-2xl mx-auto">
              A collection of production applications built and deployed — spanning enterprise SaaS, AI solutions, mobile apps, and digital tools.
            </p>
            <div className="flex items-center justify-center gap-8 mt-10">
              <div className="text-center">
                <div className="text-3xl font-bold text-gold-400" style={{ fontFamily: "'Playfair Display', serif" }}>{appsData.length}</div>
                <div className="text-sm text-[#8b94a3] mt-1">Live Apps</div>
              </div>
              <div className="w-px h-12 bg-[#e6dfd0]" />
              <div className="text-center">
                <div className="text-3xl font-bold text-gold-400" style={{ fontFamily: "'Playfair Display', serif" }}>{new Set(appsData.map(a => a.category)).size}</div>
                <div className="text-sm text-[#8b94a3] mt-1">Categories</div>
              </div>
              <div className="w-px h-12 bg-[#e6dfd0]" />
              <div className="text-center">
                <div className="flex items-center gap-1 justify-center">
                  <Globe size={20} strokeWidth={2.5} className="text-green-400" />
                  <span className="text-3xl font-bold text-green-400" style={{ fontFamily: "'Playfair Display', serif" }}>Live</span>
                </div>
                <div className="text-sm text-[#8b94a3] mt-1">All Deployed</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-8">
            {appsData.map((app, index) => (
              <a
                key={app.id}
                href={app.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group block"
                onMouseEnter={() => setHoveredApp(app.id)}
                onMouseLeave={() => setHoveredApp(null)}
                data-testid={`app-card-${app.id}`}
              >
                <div className={`relative bg-[#fffdf8] rounded-2xl border border-[#e6dfd0] overflow-hidden transition-all duration-500 ${hoveredApp === app.id ? 'border-gold-400/40 shadow-2xl shadow-gold-400/5 -translate-y-1' : 'hover:border-gold-400/20'}`}>
                  <div className={`absolute inset-0 bg-gradient-to-r ${app.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

                  <div className="relative p-8 lg:p-10">
                    <div className="flex flex-col lg:flex-row gap-8 items-start">
                      <div className="flex-shrink-0">
                        <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${app.gradient} flex items-center justify-center border border-[#e6dfd0] group-hover:scale-110 transition-transform duration-300`}>
                          <app.icon className={`w-8 h-8 ${app.accentColor}`} strokeWidth={2.5} />
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-3 mb-3">
                          <span className={`px-3 py-1 ${app.badgeColor} text-xs font-semibold rounded-full`}>
                            {app.category}
                          </span>
                          <span className="px-3 py-1 bg-green-900/30 text-green-400 text-xs font-semibold rounded-full flex items-center gap-1">
                            <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                            Live
                          </span>
                        </div>

                        <h2 className="text-2xl lg:text-3xl font-bold text-[#152846] mb-2 group-hover:text-gold-400 transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>
                          {app.name}
                        </h2>
                        <p className={`text-lg font-medium ${app.accentColor} mb-4`}>
                          {app.tagline}
                        </p>
                        <p className="text-[#5c6878] leading-relaxed mb-6 max-w-3xl">
                          {app.description}
                        </p>

                        <div className="grid sm:grid-cols-2 gap-3 mb-6">
                          {app.features.map((feature, fIndex) => (
                            <div key={fIndex} className="flex items-center gap-2">
                              <Sparkles size={14} strokeWidth={2.5} className="text-gold-400 flex-shrink-0" />
                              <span className="text-sm text-[#3e4858]">{feature}</span>
                            </div>
                          ))}
                        </div>

                        <div className="inline-flex items-center gap-2 text-gold-400 font-semibold group-hover:gap-3 transition-all duration-300">
                          Visit App
                          <ArrowRight size={18} strokeWidth={2.5} className="group-hover:translate-x-1 transition-transform" />
                          <ExternalLink size={14} strokeWidth={2.5} className="opacity-50" />
                        </div>
                      </div>

                      <div className="hidden lg:flex flex-shrink-0 items-center">
                        <div className="w-14 h-14 rounded-full bg-gold-400/10 flex items-center justify-center group-hover:bg-gold-400 transition-all duration-300">
                          <ExternalLink size={20} strokeWidth={2.5} className="text-gold-400 group-hover:text-black transition-colors" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#f3ede0]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#152846] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Interested in Building <span className="text-gold-400">Together</span>?
          </h2>
          <p className="text-[#5c6878] mb-8 max-w-xl mx-auto">
            Whether it&apos;s a SaaS platform, AI solution, or digital tool — let&apos;s discuss how technology can solve your business challenges.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gold-400 text-black font-semibold rounded-full hover:bg-gold-300 transition-all duration-300 hover:shadow-lg hover:shadow-gold-400/20"
              data-testid="apps-contact-btn"
            >
              <Mail size={18} strokeWidth={2.5} />
              Get in Touch
            </a>
            <a
              href="/booking"
              className="inline-flex items-center gap-2 px-6 py-3 border-2 border-gold-400 text-gold-400 font-semibold rounded-full hover:bg-gold-400 hover:text-black transition-all duration-300"
              data-testid="apps-booking-btn"
            >
              Book a Meeting
              <ArrowRight size={18} strokeWidth={2.5} />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Apps;
